import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Company } from '../types';
import { API_BASE_URL, forceHttpsUrl } from '../constants';


interface CompanyContextType {
  companies: Company[];
  loading: boolean;
  addCompany: (companyData: Company) => Promise<void>;
  updateCompany: (companyData: Company) => Promise<void>;
  deleteCompany: (companyId: string) => Promise<void>;
}

export const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/companies`);
            if (!response.ok) throw new Error('Failed to fetch companies');
            const data: Company[] = await response.json();
            const httpsData = data.map(company => ({
                ...company,
                logoUrl: forceHttpsUrl(company.logoUrl),
            }));
            setCompanies(httpsData);
        } catch (error) {
            console.error(error);
            // Optionally, set an error state to show in the UI
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    const addCompany = useCallback(async (companyData: Company) => {
        try {
            const response = await fetch(`${API_BASE_URL}/companies`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(companyData),
            });
            if (!response.ok) throw new Error('Failed to add company');
            const newCompany = await response.json();
            setCompanies(current => [newCompany, ...current]);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const updateCompany = useCallback(async (companyData: Company) => {
        try {
            const response = await fetch(`${API_BASE_URL}/companies/${companyData.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(companyData),
            });
            if (!response.ok) throw new Error('Failed to update company');
            const updatedCompany = await response.json();
            setCompanies(current =>
                current.map(c => (c.id === updatedCompany.id ? updatedCompany : c))
            );
        } catch (error) {
            console.error(error);
        }
    }, []);

    const deleteCompany = useCallback(async (companyId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/companies/${companyId}`, {
                method: 'DELETE',
            });

            // Se a exclusão no servidor foi bem-sucedida (status 200-299)
            if (response.ok) {
                setCompanies(current => current.filter(c => c.id !== companyId));
                alert('Empresa excluída com sucesso!');
                return;
            }

            // Se houve um erro no servidor (status 4xx ou 5xx)
            let errorMessage = `Erro de comunicação com o servidor (Status: ${response.status}).`;
            try {
                // Tenta extrair a mensagem de erro específica do JSON enviado pelo servidor
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (jsonError) {
                // Se a resposta do servidor não for um JSON válido (ex: página de erro 404 em HTML)
                console.error("Could not parse error response as JSON:", jsonError);
                errorMessage = `Erro de comunicação com o servidor (Status: ${response.status}). A resposta não é um JSON válido.`;
            }
            // Exibe a mensagem de erro mais informativa possível
            alert(errorMessage);
        } catch (networkError) {
            console.error("Network error during company deletion:", networkError);
            alert('Erro de rede. Não foi possível conectar ao servidor.');
        }
    }, []);

    return (
        <CompanyContext.Provider value={{ companies, loading, addCompany, updateCompany, deleteCompany }}>
            {children}
        </CompanyContext.Provider>
    );
};